'use strict';

// A short, local celebration. This module never reads or changes the cart or shipping rules.
(() => {
  if (window.IdolnestShippingEffects?.version === '2.0.0') return;
  window.IdolnestShippingEffects?.destroy?.();

  const styles = new Set(['halo', 'sparkle', 'ribbon', 'stamp', 'none']);
  const palette = ['#E16FAF', '#FFFFFF', '#E7C46A'];
  const active = new Set();
  const byAnchor = new WeakMap();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const svgNS = 'http://www.w3.org/2000/svg';
  const clamp = (number, min, max) => Math.min(max, Math.max(min, number));
  const finite = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;
  const notify = (callback, detail) => queueMicrotask(() => {
    try { if (typeof callback === 'function') callback(detail.cancelled ? 'cancelled' : 'finished', detail); } catch { /* Decorative feedback must not interrupt checkout. */ }
  });
  const observer = typeof MutationObserver === 'function' ? new MutationObserver(() => {
    for (const effect of [...active]) {
      if (!effect.anchor.isConnected) effect.finish('anchor-removed', true);
      else if (effect.dialog && !effect.dialog.open) effect.finish('dialog-closed', true);
    }
  }) : null;

  function cancelAll(reason) {
    for (const effect of [...active]) effect.finish(reason, true);
  }

  function span(parent, name) {
    const element = document.createElement('span');
    element.className = name;
    parent.append(element);
    return element;
  }

  function star(parent, name = '') {
    const element = span(parent, 'in-ship-fx__star ' + name);
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(svgNS, 'path');
    path.setAttribute('d', 'M10 0C11.2 7.1 12.9 8.8 20 10C12.9 11.2 11.2 12.9 10 20C8.8 12.9 7.1 11.2 0 10C7.1 8.8 8.8 7.1 10 0Z');
    path.setAttribute('fill', 'currentColor');
    svg.append(path);
    element.append(svg);
    return element;
  }

  function play(anchor, options = {}) {
    const style = styles.has(options.style) ? options.style : 'halo';
    if (!anchor || typeof anchor.append !== 'function' || !anchor.isConnected) return null;
    byAnchor.get(anchor)?.cancel();
    const dialog = anchor.closest('dialog');
    if (style === 'none' || options.enabled === false || reduced.matches) return null;
    if (document.hidden || dialog && !dialog.open || typeof anchor.animate !== 'function') return null;

    const rect = anchor.getBoundingClientRect();
    if (rect.width < 24 || rect.height < 24) return null;
    const rawDuration = finite(options.duration, 1.6);
    const duration = clamp(rawDuration > 10 ? rawDuration : rawDuration * 1000, 850, 2320);
    const requestedCount = finite(options.count, 48);
    const count = clamp(Math.round(requestedCount > 20 ? requestedCount / 4 : requestedCount), 6, 20);
    const colors = (Array.isArray(options.colors) ? options.colors : []).filter(value => typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)).slice(0, 3);
    while (colors.length < 3) colors.push(palette[colors.length]);

    const hadAnchorClass = anchor.classList.contains('in-ship-fx-anchor');
    anchor.classList.add('in-ship-fx-anchor');
    const layer = span(anchor, 'in-ship-fx in-ship-fx--' + style);
    layer.dataset.inShippingFx = style;
    layer.setAttribute('aria-hidden', 'true');
    // The decoration must stay out of flow even if its stylesheet cannot load.
    Object.assign(layer.style, { position: 'absolute', inset: '-1px', overflow: 'hidden', pointerEvents: 'none', borderRadius: 'inherit' });
    layer.style.setProperty('--in-ship-fx-primary', colors[0]);
    layer.style.setProperty('--in-ship-fx-white', colors[1]);
    layer.style.setProperty('--in-ship-fx-gold', colors[2]);
    if (getComputedStyle(anchor).position === 'static' || getComputedStyle(layer).getPropertyValue('--in-ship-fx-ready').trim() !== '1') {
      layer.remove();
      if (!hadAnchorClass) anchor.classList.remove('in-ship-fx-anchor');
      return null;
    }

    const animations = [];
    let done = false, timer = 0;
    const handle = { style, duration, cancel: () => finish('cancelled', true) };
    const effect = { anchor, dialog, finish, handle };

    function finish(reason = 'complete', cancelled = false) {
      if (done) return;
      done = true;
      clearTimeout(timer);
      for (const animation of animations) animation.cancel();
      layer.remove();
      if (!hadAnchorClass) anchor.classList.remove('in-ship-fx-anchor');
      active.delete(effect);
      if (byAnchor.get(anchor) === handle) byAnchor.delete(anchor);
      if (!active.size) observer?.disconnect();
      notify(options.onFinish, { style, reason, cancelled });
    }

    function animate(element, frames, timing = {}) {
      const animation = element.animate(frames, { duration, fill: 'both', easing: 'cubic-bezier(.22,1,.36,1)', ...timing });
      animation.finished?.catch(() => {});
      animations.push(animation);
    }

    function halo() {
      const ring = span(layer, 'in-ship-fx__halo');
      animate(ring, [
        { transform: 'scale(.985)', opacity: 0, offset: 0 },
        { transform: 'scale(1)', opacity: .88, offset: .18 },
        { transform: 'scale(1)', opacity: .48, offset: .64 },
        { transform: 'scale(1.012)', opacity: 0, offset: 1 }
      ]);
      for (const side of ['top', 'bottom']) {
        const beam = span(layer, 'in-ship-fx__beam in-ship-fx__beam--' + side);
        animate(beam, [
          { transform: 'scaleX(.05)', opacity: 0, offset: 0 },
          { transform: 'scaleX(1)', opacity: .82, offset: .3 },
          { transform: 'scaleX(.82)', opacity: 0, offset: 1 }
        ], { duration: duration * .88 });
      }
      [[6, 12], [94, 16], [9, 89], [91, 87]].forEach(([x, y], index) => {
        const point = star(layer, 'in-ship-fx__halo-star');
        point.style.left = x + '%'; point.style.top = y + '%';
        point.style.color = colors[index % 3];
        animate(point, [
          { transform: 'translate(-50%,-50%) scale(0) rotate(-25deg)', opacity: 0, offset: 0 },
          { transform: 'translate(-50%,-50%) scale(1) rotate(0deg)', opacity: .9, offset: .35 },
          { transform: 'translate(-50%,-65%) scale(.45) rotate(20deg)', opacity: 0, offset: 1 }
        ], { delay: index * 45, duration: duration * .65 });
      });
    }

    function sparkle() {
      const positions = [[7,17],[92,23],[17,91],[83,88],[29,5],[69,96],[4,63],[97,70],[47,4],[57,96],[94,7],[5,89],[36,96],[80,4],[3,42],[97,48],[20,6],[76,94],[50,97],[97,87]];
      const pointDuration = duration * .7;
      for (let index = 0; index < count; index++) {
        const point = star(layer, index % 4 === 3 ? 'in-ship-fx__star--tiny' : '');
        const [x, y] = positions[index];
        point.style.left = x + '%'; point.style.top = y + '%'; point.style.color = colors[index % 3];
        point.style.width = point.style.height = (index % 3 === 0 ? 10 : 6) + 'px';
        const drift = y > 50 ? -7 : 7;
        animate(point, [
          { transform: 'translate(-50%,-50%) scale(.1) rotate(-25deg)', opacity: 0, offset: 0 },
          { transform: 'translate(-50%,-50%) scale(1.08) rotate(4deg)', opacity: .94, offset: .28 },
          { transform: 'translate(-50%,calc(-50% + ' + drift + 'px)) scale(.35) rotate(35deg)', opacity: 0, offset: 1 }
        ], { delay: (index % 6) * duration * .035, duration: pointDuration });
      }
    }

    function ribbon() {
      const total = Math.min(16, count + count % 2);
      for (let index = 0; index < total; index++) {
        const left = index % 2 === 0, sign = left ? 1 : -1, row = Math.floor(index / 2);
        const strip = span(layer, 'in-ship-fx__ribbon');
        strip.style.left = (left ? 3 : 97) + '%';
        strip.style.top = (75 + row % 3 * 7) + '%';
        strip.style.height = (12 + row % 3 * 4) + 'px';
        strip.style.color = colors[row % 3];
        const spread = Math.min(25, rect.width * .065) + row % 3 * 3;
        const rise = Math.min(55, rect.height * .43) + row % 3 * 3;
        animate(strip, [
          { transform: 'translate(-50%,0) rotate(' + (-sign * 32) + 'deg) scaleY(.2)', opacity: 0, offset: 0 },
          { transform: 'translate(' + (sign * spread * .6) + 'px,' + (-rise * .7) + 'px) rotate(' + (sign * 40) + 'deg) scaleY(1)', opacity: .78, offset: .24 },
          { transform: 'translate(' + (sign * spread) + 'px,' + (-rise) + 'px) rotate(' + (sign * 112) + 'deg) scaleY(.82)', opacity: .66, offset: .5 },
          { transform: 'translate(' + (sign * spread * .6) + 'px,' + (-rise * .5) + 'px) rotate(' + (sign * 155) + 'deg) scaleY(.6)', opacity: 0, offset: 1 }
        ], { delay: row * duration * .025, duration: duration * .7, easing: 'cubic-bezier(.18,.5,.42,1)' });
      }
    }

    function stamp() {
      const icon = anchor.querySelector('.in-shipping-icon');
      const iconRect = icon?.getBoundingClientRect();
      const mark = span(layer, 'in-ship-fx__stamp');
      mark.style.left = (iconRect ? iconRect.left - rect.left + iconRect.width / 2 : 25) + 'px';
      mark.style.top = (iconRect ? iconRect.top - rect.top + iconRect.height / 2 : 25) + 'px';
      const seal = span(mark, 'in-ship-fx__stamp-seal');
      const svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('d', 'm6 12 4 4 8-8');
      path.setAttribute('fill', 'none'); path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '2.2'); path.setAttribute('stroke-linecap', 'round'); path.setAttribute('stroke-linejoin', 'round');
      svg.append(path); seal.append(svg);
      animate(mark, [
        { transform: 'translate(-50%,-50%) scale(1.4) rotate(-18deg)', opacity: 0, offset: 0 },
        { transform: 'translate(-50%,-50%) scale(.94) rotate(-5deg)', opacity: 1, offset: .2 },
        { transform: 'translate(-50%,-50%) scale(1) rotate(-5deg)', opacity: 1, offset: .36 },
        { transform: 'translate(-50%,-50%) scale(1) rotate(-5deg)', opacity: 1, offset: .74 },
        { transform: 'translate(-50%,-50%) scale(1.04) rotate(-3deg)', opacity: 0, offset: 1 }
      ]);
      const ring = span(layer, 'in-ship-fx__stamp-ring');
      ring.style.left = mark.style.left; ring.style.top = mark.style.top;
      animate(ring, [
        { transform: 'translate(-50%,-50%) scale(.75)', opacity: 0, offset: 0 },
        { transform: 'translate(-50%,-50%) scale(1)', opacity: .55, offset: .25 },
        { transform: 'translate(-50%,-50%) scale(1.3)', opacity: 0, offset: 1 }
      ], { duration: duration * .65 });
    }

    active.add(effect);
    byAnchor.set(anchor, handle);
    if (active.size === 1) observer?.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    try {
      ({ halo, sparkle, ribbon, stamp })[style]();
      timer = setTimeout(() => finish(), duration + 40);
    } catch {
      finish('unsupported', true);
    }
    return handle;
  }

  function onReducedChange() { if (reduced.matches) cancelAll('reduced-motion'); }
  function onVisibility() { if (document.hidden) cancelAll('not-visible'); }
  function onPageHide() { cancelAll('page-hidden'); }
  function onResize() { cancelAll('viewport-changed'); }
  function onDrawerClose(event) {
    for (const effect of [...active]) {
      if (event.target === effect.dialog || event.target?.contains?.(effect.anchor)) effect.finish('dialog-closed', true);
    }
  }
  if (typeof reduced.addEventListener === 'function') reduced.addEventListener('change', onReducedChange);
  else reduced.addListener?.(onReducedChange);
  document.addEventListener('visibilitychange', onVisibility);
  document.addEventListener('close', onDrawerClose, true);
  document.addEventListener('idolnest:drawer:closing', onDrawerClose, true);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('resize', onResize);

  window.IdolnestShippingEffects = {
    version: '2.0.0',
    play,
    cancelAll: () => cancelAll('cancelled'),
    destroy() {
      cancelAll('destroyed'); observer?.disconnect();
      if (typeof reduced.removeEventListener === 'function') reduced.removeEventListener('change', onReducedChange);
      else reduced.removeListener?.(onReducedChange);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('close', onDrawerClose, true);
      document.removeEventListener('idolnest:drawer:closing', onDrawerClose, true);
      window.removeEventListener('pagehide', onPageHide); window.removeEventListener('resize', onResize);
      delete window.IdolnestShippingEffects;
    }
  };
})();
